import { computed, toValue, type MaybeRef, type Ref } from 'vue'

import { useRealtimeSubscription, type RealtimeFrame } from '@ja/realtime'

export interface CommentPayload {
  id: string
  user_id: string
  message: string
  type: string
  created_at: string
  updated_at: string
  user: { id: string; full_name: string | null; email: string }
  reactions: Array<{ id: string; reaction: string; user_id: string }>
  read_by_current_user?: boolean
  can_edit?: boolean
  can_delete?: boolean
  attachments?: Array<{
    uuid: string
    file_name: string
    mime_type: string
    size: number
    original_url: string
    thumb_url: string
  }>
}

interface CommentFramePayload {
  action: 'comment_created' | 'comment_updated' | 'comment_deleted'
  referenceType: string
  referenceId: string
  commentId: string
  comment?: CommentPayload
  actorId: string | number | null
  timestamp: string
}

export interface UseCommentsChannelOptions {
  onCreated?: (comment: CommentPayload) => void
  onUpdated?: (comment: CommentPayload) => void
  onDeleted?: (commentId: string) => void
}

export interface UseCommentsChannelReturn {
  isConnected: Ref<boolean>
  unsubscribe: () => void
}

/**
 * Vue composable for real-time comment events.
 *
 * Subscribes to `crud:<referenceType>:<referenceId>:comments` and dispatches
 * on wire events `crud.comment_created` / `crud.comment_updated` /
 * `crud.comment_deleted`.
 */
export function useCommentsChannel(
  modelType: MaybeRef<string>,
  modelId: MaybeRef<string | number>,
  options: UseCommentsChannelOptions = {},
): UseCommentsChannelReturn {
  const { onCreated, onUpdated, onDeleted } = options

  const channel = computed(() => {
    const mt = toValue(modelType)
    const mi = toValue(modelId)
    if (!mt || mi === undefined || mi === null || mi === '') return ''
    return `crud:${mt}:${mi}:comments`
  })

  const { isConnected, unsubscribe } = useRealtimeSubscription<CommentFramePayload>(channel, {
    onMessage(frame: RealtimeFrame<CommentFramePayload>) {
      if (frame.event === 'crud.comment_created' && frame.data.comment) {
        onCreated?.(frame.data.comment)
      } else if (frame.event === 'crud.comment_updated' && frame.data.comment) {
        onUpdated?.(frame.data.comment)
      } else if (frame.event === 'crud.comment_deleted') {
        onDeleted?.(frame.data.commentId)
      }
    },
  })

  return { isConnected, unsubscribe }
}
